
import { Card, CardContent } from '@/components/ui/card';
import DestinationCard from '@/components/destination/DestinationCard';
import { HostCard } from '@/components/destination/HostCard';
import { motion } from 'framer-motion';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useEffect } from 'react';

interface SearchResultsProps {
  destinations: any[];
  hosts: any[];
  loading: boolean;
  onDestinationClick: (id: string) => void;
  currentPage: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export const SearchResults = ({
  destinations,
  hosts,
  loading,
  onDestinationClick,
  currentPage,
  totalCount,
  itemsPerPage,
  onPageChange
}: SearchResultsProps) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPagination = () => {
    // Don't show pagination if there's no need
    if (totalCount <= itemsPerPage) {
      return null;
    }
    
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => handlePageChange(currentPage - 1)}
              className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              role="button"
              tabIndex={0}
              aria-disabled={currentPage <= 1}
            />
          </PaginationItem>

          {startPage > 1 && (
            <>
              <PaginationItem>
                <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
              </PaginationItem>
              {startPage > 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
            </>
          )}

          {pages.map((page) => (
            <PaginationItem key={page}>
              <PaginationLink
                isActive={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          <PaginationItem>
            <PaginationNext
              onClick={() => handlePageChange(currentPage + 1)}
              className={currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              role="button"
              tabIndex={0}
              aria-disabled={currentPage >= totalPages}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((n) => (
          <Card key={n} className="overflow-hidden animate-pulse">
            <CardContent className="p-0">
              <div className="h-48 bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {(destinations.length > 0 || hosts.length > 0) ? (
        <motion.div
          initial="hidden"
          animate="show"
          variants={container}
          className="space-y-8"
        >
          {destinations.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">
                Destinations
                <span className="text-gray-500 text-lg ml-2">
                  ({totalCount} results)
                </span>
                <span className="text-gray-400 text-sm ml-2">
                  Page {currentPage} of {totalPages}
                </span>
              </h2>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                variants={container}
              >
                {destinations.map((destination) => (
                  <motion.div key={destination.id} variants={item}>
                    <DestinationCard
                      destination={destination}
                      onClick={() => onDestinationClick(destination.id)}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}

          {hosts.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">Local Hosts</h2>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                variants={container}
              >
                {hosts.map((host) => (
                  <motion.div key={host.id} variants={item}>
                    <HostCard 
                      host={host}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              {renderPagination()}
            </div>
          )}
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <h3 className="text-xl font-medium text-gray-600 mb-2">
            No results found
          </h3>
          <p className="text-gray-500">
            Try adjusting your filters or search terms
          </p>
        </motion.div>
      )}
    </div>
  );
};
